import { db } from "../dexie";
import { addDays, startOfWeek, startOfDay } from "date-fns";
import { Goal, goalRecordSchema, Instance } from "../types";
import { getLocalDate } from "../utils/localDate";
import { HabitInstantiator } from "../models/HabitInstantiator";
import { v4 as uuid } from "uuid";
import { Habits } from "../services/Habits";
import { GoalSummary, WeekReport } from "../models/GoalSummary";
import { Sync } from "../sync";

export async function getGoal(goalId: string) {
  const goal = await db.goals.get(goalId);

  if (!goal) {
    throw new Error("Goal not found");
  }

  return goal;
}

/** Domain goal from local DB, or null if missing (for detail views that should not throw). */
export async function getGoalRecord(goalId: string): Promise<Goal | null> {
  const row = await db.goals.get(goalId);
  return row ? goalRecordSchema.encode(row) : null;
}

export type UpsertGoalType = Omit<Goal, "id"> & { id?: string };

export async function upsertGoal(goal: UpsertGoalType) {
  const { id = uuid(), ...data } = goal;
  await Sync.upsert({ ...data, id, type: "goal" });
}

export async function getGoals() {
  const goals = (await db.goals.toArray()).map((goal) => goalRecordSchema.encode(goal));

  // Get habit tasks for each goal
  const goalHabits = await Habits.getAll().groupBy("goalId");

  const yesterday = addDays(new Date(), -1);
  const startDate = getLocalDate(startOfWeek(yesterday, { weekStartsOn: 1 }));
  const endDate = getLocalDate(startOfDay(yesterday));

  const instancesByDate = await HabitInstantiator.getInstancesByDateRange(startDate, endDate);

  const fourWeekInstances = await HabitInstantiator.getInstancesByDateRange(
    getLocalDate(addDays(startDate, -29)),
    getLocalDate(addDays(startDate, -1)),
  );

  return goals.map((goal) => {
    const habits = (goalHabits[goal.id] ?? []).map((habit) => {
      const { dayReports, summary } = GoalSummary.summariseHabit(habit, instancesByDate);
      const weekSummaries: WeekReport[] = [];
      for (let i = 0; i < 28; i += 7) {
        const weekInstances: Instance[][] = fourWeekInstances.slice(i, i + 7);
        const { summary: weekSummary } = GoalSummary.summariseHabit(habit, weekInstances);
        weekSummaries.push(weekSummary);
      }
      return { ...habit, dayReports, summary, weekSummaries };
    });

    const summary = GoalSummary.sumWeekReports(habits.map((habit) => habit.summary));

    const weekSummaries: WeekReport[] = [];
    for (let i = 0; i < 4; i++) {
      weekSummaries.push(GoalSummary.sumWeekReports(habits.map((h) => h.weekSummaries[i])));
    }

    return { ...goal, habits, summary, weekSummaries };
  });
}
